import { AppProvider, Button } from '@kcftech/design-system';
import { GeneralDetailError } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { DefaultButton } from 'components/button/DefaultButton';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { ReactNode, useMemo, useState } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { contextualModalZIndex } from 'styles/variables';

export const animationDurationMs = 200;

const SlideIn = keyframes`
    from { transform: translateX(100%) }
    to   { transform: translateX(0%) }
`;
const SlideOut = keyframes`
    from { transform: translateX(0%) }
    to   { transform: translateX(100%) }
`;

type WrapperProps = {
    /**
     * Whether the wrapper should be animating in or out.
     */
    $isClosing: boolean;
    $width: number;
};

const ContextualModalWrapper = styled.form<WrapperProps>`
    box-sizing: border-box;
    background: var(--background-paper-elevation-2);
    color: var(--text-primary);
    box-shadow: var(--elevation-200);
    animation: ${animationDurationMs}ms ease
        ${(props: WrapperProps) => (props.$isClosing ? SlideOut : SlideIn)};

    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 100%;
    z-index: ${contextualModalZIndex};
    /* The modal itself should not scroll, the content is independently
     * scrollable in the Y direction.
     */
    overflow: hidden;
    /* On mobile, components may overflow the width, so we need to set a
     * maximum width so that there is no scrolling
     */
    max-width: 100vw;

    @media ${MediaQueries.smallAndUp} {
        position: absolute;
        height: 100%;
        width: ${({ $width }: WrapperProps) => `${$width}px`};
    }
`;

const ModalContent = styled.div`
    overflow-y: auto;
`;

const ModalHeader = styled.header`
    display: flex;
    align-items: center;
    padding: 4rem 4rem 3rem 4rem;

    @media (max-height: 420px) {
        padding-top: 1.5rem;
        padding-bottom: 1.5rem;
    }

    & > svg:first-child,
    & > img:first-child {
        padding-right: 0.8ch;
        height: 2rem;
    }

    & > * {
        height: 2.5rem;
    }
`;

const ModalFooter = styled.footer`
    padding: 4rem;
    text-align: right;

    @media (max-height: 420px) {
        padding-top: 1.5rem;
        padding-bottom: 1.5rem;
    }
`;

const ModalOverlayContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${contextualModalZIndex};
    width: 100vw;
    height: 100vh;
`;

export type ModalReasonForClose = 'saved' | 'cancelled';

export type ModalCloseEvent<TSaveResult> = Readonly<{
    reason: ModalReasonForClose;
    result: TSaveResult | boolean | undefined;
}>;

export type RenderContentInfo = Readonly<{
    /**
     * If a save is in progress.
     */
    isSaving: boolean;
    /**
     * An error generated by the `onSave` callback.
     */
    saveError: Error | undefined;
}>;

export type StatelessContextualModalProps<TSaveResult> = {
    /**
     * The title of the modal
     */
    title: string;
    /**
     * The props for an icon that appears to the left of the title of the modal
     */
    titleIcon?: ReactNode;
    /**
     * An identifier unique to this modal, used for accessibility and testing.
     * For example, an ID of `my-settings` will turn into an id of `my-settings-contextual-modal-*`,
     * where `*` is title `title`, `header`, etc. or just un-suffixed for the modal itself.
     */
    id: string;
    /**
     * Callback to render modal content.
     */
    content: (info: RenderContentInfo) => ReactNode;
    /**
     * Enables/disables save button (defaults to `true).
     */
    canSave?: boolean;
    /**
     * Callback that will run after the modal has been closed and is no longer visible.
     * The callback will be called with the reason for the closure.
     */
    onClose: (event: ModalCloseEvent<TSaveResult>) => void;
    /**
     * Callback that will run when the user clicks the save button on the
     * modal to save the form, before the modal closes.  The callback can return a
     * boolean to indicate whether or not the save was successful. If the return
     * value is `false`, then the modal will not close and can be saved again.
     * If a callback is not supplied, the save button will not be visible.
     */
    onSave?: () => TSaveResult | boolean | Promise<TSaveResult | boolean>;
    /**
     * When true, overlay is visible (defaults to `false`).
     */
    overlay?: boolean;
    /**
     * Label for save button (defaults to `Save`).
     */
    saveLabel?: ReactNode;
    /**
     * Label for cancel button (defaults to `Cancel`).
     */
    cancelLabel?: ReactNode;
    /**
     * The modal width in `px` (defaults to `450`).
     */
    width?: number;
};

export function StatelessContextualModal<TSaveResult>({
    title,
    titleIcon,
    content,
    id,
    canSave = true,
    onClose,
    onSave,
    overlay = false,
    saveLabel = 'Save',
    cancelLabel = 'Cancel',
    width = 450,
}: StatelessContextualModalProps<TSaveResult>) {
    const [isClosing, setClosing] = useState(false);
    const [saveResult, setSaveResult] = useState<TSaveResult | boolean>();
    const [reasonForClose, setReasonForClose] = useState<ModalReasonForClose>(
        'cancelled'
    );

    const closeModal = (reason: ModalReasonForClose) => {
        setClosing(true);
        setReasonForClose(reason);
    };

    const { error, isPending: isSaving, mutate } = useMutation<
        boolean | TSaveResult | undefined,
        GeneralDetailError,
        void,
        unknown
    >({
        mutationFn: async () => onSave?.(),

        onSuccess: (result) => {
            if (result !== false) {
                if (result !== undefined) {
                    setSaveResult(result);
                }
                closeModal('saved');
            }
        },
    });

    const saveError = useMemo(() => {
        if (!error) {
            return undefined;
        }
        // when the error Status is a 400 (Bad Request) we get a useful response object
        // with a customized error code from the API. We want to use that if possible.
        return error.Detail && error.Status === 400
            ? new Error(error.Detail)
            : new Error('Failed to save');
    }, [error]);

    const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (canSave) {
            mutate();
        }
    };

    const onAnimationEnd = () => {
        if (isClosing) {
            onClose({ reason: reasonForClose, result: saveResult });
        }
    };

    const renderInfo: RenderContentInfo = { isSaving, saveError };

    const titleId = `${id}-title`;
    const headerId = `${id}-header`;
    const contentId = `${id}-content`;
    const footerId = `${id}-footer`;

    return createPortal(
        <AppProvider>
            <ModalOverlayContainer>
                <div
                    className="k-overlay"
                    style={{
                        zIndex: contextualModalZIndex,
                        background: overlay ? undefined : 'none',
                    }}
                />
                <ContextualModalWrapper
                    id={id}
                    data-testid={id}
                    data-cy={`${id}-contextual-modal`}
                    aria-modal="true"
                    aria-labelledby={titleId}
                    $isClosing={isClosing}
                    onAnimationEnd={onAnimationEnd}
                    onSubmit={onFormSubmit}
                    $width={width}
                >
                    <ModalHeader id={headerId}>
                        {titleIcon}
                        <div
                            role="heading"
                            className="f-20 flex-grow-1"
                            style={{ height: '2.5rem' }}
                            aria-level={2}
                            id={titleId}
                        >
                            {title}
                        </div>
                    </ModalHeader>
                    <ModalContent id={contentId} className="flex-grow-1 px-40">
                        {content(renderInfo)}
                    </ModalContent>
                    <ModalFooter id={footerId}>
                        <Button
                            appearance="secondary"
                            type="button"
                            disabled={isSaving || isClosing}
                            onClick={() => closeModal('cancelled')}
                            className="mr-10"
                        >
                            {cancelLabel}
                        </Button>
                        {onSave && (
                            <DefaultButton
                                type="submit"
                                disabled={!canSave || isSaving || isClosing}
                            >
                                {saveLabel}
                            </DefaultButton>
                        )}
                    </ModalFooter>
                </ContextualModalWrapper>
            </ModalOverlayContainer>
        </AppProvider>,
        document.body
    );
}
